import React, { useEffect, useState } from 'react';
import DashBoardChart from './Components/DashBoardChart';
import {
	getDashBoardPie,
	getDashBoardWaterFall,
	getProgramSavingsMainTable,
	getProgramSavingsMicroMacroTable,
	getVendorCount,
	getVendorEliminationReport,
	getVendorReductionReport,
} from 'utils/apiRequests';
import {
	combinePieData,
	combineWaterFallData,
	convertMicroMacroData,
	getCustomTitle,
	getSubtitle,
	GL_ACCOUNT_MASTER_DATA,
	PIE_DATA,
	PIE_OPT,
	PROGRAMS_SAVINGS_DASHBOARD,
	PROGRAMS_SAVINGS_MICRO_MACRO_DASHBOARD,
	VENDOR_COUNT,
	VENDOR_ELIMINATION_REPORT,
	WATERFALL_DATA,
} from 'SpendController/utils';
import NavBar from './Components/NavBar';
import LoadingSpinner from 'SpendController/Components/LoadingSpinner/LoadingSpinner';
import { Container } from 'SpendController/Components/Components.styles';
import {
	CenteredContainer,
	NavDiv,
	SearchWrapper,
} from './FinanceReport.styles';
import ProgramSavings from './Components/ProgramSavingsDasBoard/ProgramSavings';
import VendorEliminationReport from './Components/VendorEliminationReport/VendorEliminationReport';
import GLAccountMaster from './Components/GLAccountMaster/GLAccountMaster';
import { SearchInput } from 'components/SearchInput';
import { TooltipIcon } from 'BudgetTracker/HeaderSummary/HeaderSummary.styles';
import { Tooltip } from 'react-tippy';
import { ApIcon, colors } from '@alixpartners/ui-components';
import { useSpendControllerFiltersContext } from 'SpendController/Contexts/SpendControllerFiltersProvider';
import { TooltipContent } from 'BudgetTracker/SecondaryNav/SecondaryNav.styles';
import { spendFormatter } from 'utils/filterNames';

function FinanceReport() {
	const [waterfallData, setWaterFallData] = useState(WATERFALL_DATA);
	const [pieData, setPieData] = useState({
		chartData: PIE_DATA,
		kpiData: [0, 0],
		options: PIE_OPT,
	});
	const [vendorCountData, setVendorCountData] = useState(VENDOR_COUNT);
	const [isLoading, setLoading] = useState(false);
	const [programSavingsDashboard, setProgramSavingsDashboard] = useState({
		columnDefs: PROGRAMS_SAVINGS_DASHBOARD,
		rowData: [],
	});
	const [
		programSavingsMicroMacroData,
		setProgramSavingsMicroMacroData,
	] = useState({
		columnDefs: PROGRAMS_SAVINGS_MICRO_MACRO_DASHBOARD,
		rowData: [],
	});
	const [vendorElimationReportData, setVendorElimationReportData] = useState({
		columnDefs: VENDOR_ELIMINATION_REPORT,
		rowData: [],
	});
	const [vendorReductionReportData, setVendorReductionReportData] = useState({
		columnDefs: VENDOR_ELIMINATION_REPORT,
		rowData: [],
	});
	const [glAccountMasterData] = useState(GL_ACCOUNT_MASTER_DATA);
	const [navNo, setNavNumber] = useState(0);
	const { searchOptions } = useSpendControllerFiltersContext();
	const [selectedSearchOption, setSelectedSearchOption] = useState(null);
	const [savingsCategory, setSavingCategory] = useState({
		label: 'Identified Savings',
		value: 1,
	});
	useEffect(
		() => {
			getDashBoardChartData();
			getProgramSavingsData();
			handleVendorEliminationData();
			handleVendorReductionData();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedSearchOption, savingsCategory]
	);

	const getDashBoardChartData = async () => {
		setLoading(true);
		const waterFallRespone = await getDashBoardWaterFall(selectedSearchOption);
		const pieChartPayload = {
			search: selectedSearchOption === null ? '' : selectedSearchOption.value,
			type: savingsCategory.value,
		};
		const pieResponse = await getDashBoardPie(pieChartPayload);
		const vendorCountResponse = await getVendorCount(selectedSearchOption);

		const formattedWaterFallData = combineWaterFallData(waterFallRespone.data);
		const formattedPieData = combinePieData(
			pieResponse.data,
			pieData.chartData
		);

		const initialSpend = waterFallRespone.data.initial_Baseline_Spend;
		const sumSaving = Object.values(pieResponse.data).reduce(
			(acc, value) => acc + value,
			0
		);

		const formattedSavingOpportunity = (sumSaving / initialSpend) * 100;
		setWaterFallData(formattedWaterFallData);
		setPieData({
			chartData: formattedPieData,
			kpiData: [spendFormatter(sumSaving, true), formattedSavingOpportunity],
			options: {
				...pieData.options,
				subtitle: {
					useHTML: true,
					text: getSubtitle([
						spendFormatter(sumSaving, true),
						formattedSavingOpportunity,
					]),
					floating: true,
					verticalAlign: 'middle',
					y: 21,
				},
				title: {
					text: getCustomTitle([
						spendFormatter(sumSaving, true),
						formattedSavingOpportunity,
					]),
					useHTML: true,
					align: 'left',
				},
				series: [
					{
						type: 'pie',
						name: 'Total',
						data: formattedPieData.map(dataPoint => {
							if (dataPoint.y === 0) {
								dataPoint.dataLabels.enabled = false;
							}
							return dataPoint;
						}),
						size: 250,
						innerSize: '75%',
					},
				],
			},
		});
		setVendorCountData(vendorCountResponse.data);
	};
	const getProgramSavingsData = async () => {
		const programSavingsMainDataResponse = await getProgramSavingsMainTable(
			selectedSearchOption
		);
		setProgramSavingsDashboard(prevstate => ({
			...prevstate,
			rowData: programSavingsMainDataResponse.data,
		}));
		const programSavingsMicroMacroDataResponse = await getProgramSavingsMicroMacroTable(
			selectedSearchOption
		);
		setProgramSavingsMicroMacroData(prevstate => ({
			...prevstate,
			rowData: convertMicroMacroData(programSavingsMicroMacroDataResponse.data),
		}));
		setLoading(false);
	};
	const handleVendorEliminationData = async () => {
		const vendorEliminationResponse = await getVendorEliminationReport(
			selectedSearchOption
		);
		setVendorElimationReportData(prevstate => ({
			...prevstate,
			rowData: vendorEliminationResponse.data,
		}));
	};
	const handleVendorReductionData = async () => {
		const vendorReductionResponse = await getVendorReductionReport(
			selectedSearchOption
		);
		setVendorReductionReportData(prevstate => ({
			...prevstate,
			rowData: vendorReductionResponse.data,
		}));
	};
	const handleNavComponentChange = () => {
		switch (navNo) {
			case 0:
				return (
					<ProgramSavings
						programSavingsMicroMacroData={programSavingsMicroMacroData}
						programSavingsDashboard={programSavingsDashboard}
					/>
				);
			case 1:
				return (
					<VendorEliminationReport
						vendorElimationReportData={vendorElimationReportData}
					/>
				);
			case 2:
				return (
					<VendorEliminationReport
						vendorElimationReportData={vendorReductionReportData}
					/>
				);
			case 3:
				return <GLAccountMaster data={glAccountMasterData} />;
			default:
				return <span />;
		}
	};
	return (
		<>
			<Container>
				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<SearchWrapper>
						<SearchInput
							placeholder="Search..."
							placement="bottom-end"
							charCount={2}
							maxMatchesNumber={10}
							showIcon
							isSelectable
							isDismissible
							selectedOption={selectedSearchOption}
							options={searchOptions}
							onChange={e => setSelectedSearchOption(e)}
						/>
						<TooltipIcon>
							<Tooltip
								position="bottom"
								animation="fade"
								arrow
								theme="light"
								trigger="click"
								html={
									<TooltipContent>
										Enter Business Unit, Final Category, Entity, Supplier Name
										or Spend Owner, to filter the data in the charts and tables.
									</TooltipContent>
								}
								popperOptions={{
									modifiers: {
										preventOverflow: {
											enabled: true,
											boundariesElement: 'window',
										},
									},
								}}
							>
								<ApIcon
									iconName="outline_info"
									iconColor={colors.grey5}
									iconSize={16}
								/>
							</Tooltip>
						</TooltipIcon>
					</SearchWrapper>
				</div>
				<LoadingSpinner visible={isLoading} message={`Loading`} />
				<DashBoardChart
					waterfallData={waterfallData}
					pieData={pieData.chartData}
					vendorCountData={vendorCountData}
					savingData={pieData.kpiData}
					option1={pieData.options}
					savingsCategory={savingsCategory}
					setSavingCategory={setSavingCategory}
				/>
				<NavDiv>
					<NavBar navNo={navNo} setNavNumber={setNavNumber} />
					<CenteredContainer>{handleNavComponentChange()}</CenteredContainer>
				</NavDiv>
			</Container>
		</>
	);
}

export default FinanceReport;
